import React from 'react'
import { Box } from '@mui/material'
import { PlayIcon } from '@heroicons/react/24/solid'
import { INTERAX_COLORS } from '../../../../constants/colors'

interface CircularPlayButtonProps {
  size?: number
  onClick?: () => void
  className?: string
}

/**
 * CircularPlayButton - Animated circular play button with glow effect
 * Matches the design from the onboarding step images
 */
export const CircularPlayButton: React.FC<CircularPlayButtonProps> = ({ size = 120, onClick, className = '' }) => {
  return (
    <Box
      className={className}
      onClick={onClick}
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': onClick
          ? {
              transform: 'scale(1.05)'
            }
          : {}
      }}
    >
      {/* Outer glow rings */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${INTERAX_COLORS.sun}40 0%, ${INTERAX_COLORS.sun}20 30%, transparent 70%)`,
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 0.6,
              transform: 'scale(1)'
            },
            '50%': {
              opacity: 0.8,
              transform: 'scale(1.1)'
            }
          }
        }}
      />

      {/* Middle ring */}
      <Box
        sx={{
          position: 'absolute',
          width: '90%',
          height: '90%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${INTERAX_COLORS.sun}60 0%, ${INTERAX_COLORS.sun}30 50%, transparent 80%)`,
          animation: 'pulse 2s ease-in-out infinite 0.3s'
        }}
      />

      {/* Inner ring */}
      <Box
        sx={{
          position: 'absolute',
          width: '80%',
          height: '80%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${INTERAX_COLORS.sun}80 0%, ${INTERAX_COLORS.sun}40 60%, transparent 90%)`,
          animation: 'pulse 2s ease-in-out infinite 0.6s'
        }}
      />

      {/* White border rings */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: `2px solid ${INTERAX_COLORS.white}40`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '8px',
            left: '8px',
            right: '8px',
            bottom: '8px',
            borderRadius: '50%',
            border: `1px solid ${INTERAX_COLORS.white}60`
          }
        }}
      />

      {/* Main play button */}
      <Box
        sx={{
          width: '60%',
          height: '60%',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${INTERAX_COLORS.sun} 0%, #ff8c00 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 20px ${INTERAX_COLORS.sun}60`,
          position: 'relative',
          zIndex: 10,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: `0 6px 30px ${INTERAX_COLORS.sun}80`,
            transform: 'scale(1.05)'
          }
        }}
      >
        <PlayIcon
          className="w-8 h-8 text-white"
          style={{
            marginLeft: '3px' // Slight offset to center the play icon visually
          }}
        />
      </Box>
    </Box>
  )
}
